import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import type { AudiHeaderLogoProps } from '../../interfaces/header-components.interfaces';
import type { HeaderStyledLogoProps } from '../../interfaces/header-styled-components.interfaces';
import { createBreakpointStyles } from '../../services';
import { timeM } from '../AudiHeader/AudiHeaderStyleUtils';
import { logoTrackingData } from '../../utils/tracking';
import { ServiceContext } from '../../context/ServiceContext';
import { responsiveStyles } from '../../utils/responsiveStyles';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';

const headerStyledLogoStyles = (): Array<{
  maxNavItems: number;
  style: unknown;
}> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      'flex-grow': { l: '0' },
      'margin-left': {
        l: '0',
      },
      'margin-right': {
        l: 'var(--one-header-space-xl)',
      },
      opacity: { l: '1' },
      'pointer-events': { l: 'auto' },
      transition: { l: 'none' },
    },
  },
  {
    maxNavItems: 5,
    style: {
      'flex-grow': { xl: '0' },
      'margin-left': {
        xl: '0',
      },
      'margin-right': {
        xl: 'var(--one-header-space-xl)',
      },
      opacity: { xl: '1' },
      'pointer-events': { xl: 'auto' },
      transition: { xl: 'none' },
    },
  },
  {
    maxNavItems: 8,
    style: {
      'flex-grow': { xxl: '0' },
      'margin-left': {
        xxl: '0',
      },
      'margin-right': {
        xxl: 'var(--one-header-space-xl)',
      },
      opacity: { xxl: '1' },
      'pointer-events': { xxl: 'auto' },
      transition: { xxl: 'none' },
    },
  },
  {
    maxNavItems: -1, // > 8 navItems
    style: null,
  },
];

const HeaderStyledLogo = styled.div<HeaderStyledLogoProps>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-white)' : ''};
  line-height: 0;
  margin-bottom: var(--one-header-space-l);
  margin-left: auto;
  margin-right: ${(props) => (props.headerNavigationItemsAmount ? 'auto' : '14%')};
  margin-top: var(--one-header-space-l);
  ${({ headerNavigationItemsAmount, theme }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerStyledLogoStyles()),
      theme.breakpoints,
    )}
  opacity: ${({ isOpen }): string => (isOpen ? '0' : '1')};
  pointer-events: ${({ isOpen }): string => (isOpen ? 'none' : 'auto')};
  text-align: center;
  transition: opacity ${timeM} ${({ theme }): string => theme.easing.easingDefault};

  & a {
    display: inline-block;
  }

  & a svg {
    height: var(--audi-rings-logo-height);
    width: var(--audi-rings-logo-width);
  }

  & a strong {
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    overflow: hidden;
    position: absolute;
    width: 1px;
  }

  @media (min-width: 1440px) {
    margin-left: ${(props) => (props.headerNavigationItemsAmount ? 'auto' : '0px')};
    margin-right: ${(props) => (props.headerNavigationItemsAmount ? '' : 'auto')};
  }
`;

const AudiHeaderLogo: React.FC<AudiHeaderLogoProps> = (props) => {
  const { trackingService } = useContext(ServiceContext);
  const { headerNavigationItemsAmount, isLoading, isOpen, logoLink } = props;

  const logoHandleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackingService?.track(logoTrackingData(window?.location.origin));
    window.location.href = logoLink?.Url || window?.location.origin;
  };
  if (isLoading) {
    return (
      <HeaderStyledLogo headerNavigationItemsAmount={headerNavigationItemsAmount} isOpen={isOpen}>
        <AudiHeaderLogoImage />
      </HeaderStyledLogo>
    );
  }

  return (
    <HeaderStyledLogo headerNavigationItemsAmount={headerNavigationItemsAmount} isOpen={isOpen}>
      <a
        aria-label={logoLink?.Text || 'Audi Logo'}
        href={logoLink?.Url}
        title={logoLink?.Text}
        onClick={logoHandleClick}
        className={NAVIGATION_CLASSNAME}
      >
        <AudiHeaderLogoImage />
        <strong>{logoLink?.Text}</strong>
      </a>
    </HeaderStyledLogo>
  );
};

const AudiHeaderLogoImage: React.FC = () => {
  const themeContext = useContext(ThemeContext);
  return themeContext.type === 'legacy' ? (
    <svg height="24" width="69" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M56.927 0a11.798 11.798 0 00-7.495 2.671A11.906 11.906 0 0041.9 0a11.719 11.719 0 00-7.494 2.671C32.347 1.006 29.745 0 26.91 0a11.798 11.798 0 00-7.494 2.671C17.358 1.006 14.756 0 11.92 0 5.32 0 0 5.303 0 11.884c0 6.58 5.32 11.884 11.921 11.884 2.835 0 5.475-1.007 7.495-2.671 2.058 1.664 4.66 2.67 7.494 2.67 2.835 0 5.475-1.006 7.495-2.67a11.906 11.906 0 007.533 2.67c2.874 0 5.475-1.006 7.533-2.67 2.058 1.664 4.66 2.67 7.495 2.67 6.601 0 11.921-5.302 11.921-11.883C68.848 5.342 63.528 0 56.927 0zm-7.495 17.226a9.126 9.126 0 01-1.708-5.342c0-1.974.621-3.832 1.708-5.342a9.126 9.126 0 011.709 5.342c0 2.013-.66 3.832-1.709 5.342zm-15.027 0a9.126 9.126 0 01-1.709-5.342c0-1.974.621-3.832 1.709-5.342a9.126 9.126 0 011.708 5.342c0 2.013-.66 3.832-1.708 5.342zm-15.028 0a9.126 9.126 0 01-1.709-5.342c0-1.974.622-3.832 1.709-5.342a9.126 9.126 0 011.708 5.342c0 2.013-.66 3.832-1.708 5.342zM2.602 11.884c0-5.071 4.116-9.213 9.242-9.213a9.22 9.22 0 015.63 1.897c-1.592 2.013-2.524 4.529-2.524 7.316 0 2.748.932 5.303 2.524 7.316-1.553 1.2-3.495 1.897-5.63 1.897-5.087 0-9.242-4.103-9.242-9.213zM21.24 19.2c1.592-2.013 2.524-4.529 2.524-7.316 0-2.749-.932-5.303-2.524-7.316 1.553-1.2 3.495-1.897 5.63-1.897 2.136 0 4.078.697 5.63 1.897-1.591 2.013-2.523 4.529-2.523 7.316 0 2.748.932 5.303 2.524 7.316-1.553 1.2-3.495 1.897-5.63 1.897-2.136 0-4.078-.697-5.631-1.897zm15.028 0c1.592-2.013 2.524-4.529 2.524-7.316 0-2.749-.932-5.303-2.524-7.316 1.553-1.2 3.494-1.897 5.63-1.897a9.22 9.22 0 015.63 1.897c-1.591 2.013-2.523 4.529-2.523 7.316 0 2.748.932 5.303 2.524 7.316-1.554 1.2-3.495 1.897-5.63 1.897a9.22 9.22 0 01-5.631-1.897zm20.658 1.897a9.22 9.22 0 01-5.63-1.897c1.591-2.013 2.523-4.529 2.523-7.316 0-2.749-.932-5.303-2.524-7.316 1.554-1.2 3.495-1.897 5.63-1.897 5.088 0 9.243 4.103 9.243 9.213 0 5.11-4.155 9.213-9.242 9.213z"
        fill="#000"
        fillRule="evenodd"
      />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="69" height="24" viewBox="0 0 69 24" fill="none">
      <path
        d="M57.0623 21.3142C54.9409 21.3142 52.9856 20.5922 51.422 19.3822C53.0087 17.3448 53.9585 14.7826 53.9585 12C53.9585 9.21768 53.0087 6.65544 51.422 4.61784C52.9856 3.408 54.9409 2.68584 57.0623 2.68584C62.1714 2.68584 66.3281 6.86424 66.3281 12C66.3281 17.136 62.1714 21.3142 57.0623 21.3142ZM36.3802 19.3822C37.9672 17.3448 38.917 14.7826 38.917 12C38.917 9.21768 37.9672 6.65544 36.3804 4.61784C37.9438 3.408 39.8997 2.68584 42.0208 2.68584C44.1421 2.68584 46.0975 3.408 47.6611 4.61784C46.0743 6.65544 45.1246 9.21768 45.1246 12C45.1246 14.7826 46.0743 17.3448 47.6611 19.3822C46.0975 20.5922 44.1421 21.3142 42.0208 21.3142C39.8997 21.3142 37.9438 20.5922 36.3802 19.3822ZM21.3387 19.3822C22.9257 17.3448 23.8754 14.7826 23.8754 12C23.8754 9.21768 22.9257 6.65544 21.3389 4.61784C22.9023 3.408 24.8581 2.68584 26.9792 2.68584C29.1003 2.68584 31.0562 3.408 32.6196 4.61784C31.0328 6.65544 30.083 9.21768 30.083 12C30.083 14.7826 31.0328 17.3448 32.6196 19.3822C31.0562 20.5922 29.1003 21.3142 26.9792 21.3142C24.8581 21.3142 22.9023 20.5922 21.3387 19.3822ZM2.6719 12C2.6719 6.86424 6.82861 2.68584 11.9377 2.68584C14.0588 2.68584 16.0147 3.408 17.578 4.61784C15.9913 6.65544 15.0415 9.21768 15.0415 12C15.0415 14.7826 15.9913 17.3448 17.578 19.3822C16.0147 20.5922 14.0588 21.3142 11.9377 21.3142C6.82861 21.3142 2.6719 17.136 2.6719 12ZM19.4585 17.4305C18.3619 15.9005 17.7134 14.0256 17.7134 12C17.7134 9.97464 18.3619 8.09952 19.4585 6.56952C20.5551 8.09952 21.2035 9.97464 21.2035 12C21.2035 14.0256 20.5551 15.9005 19.4585 17.4305ZM34.5 17.4305C33.4034 15.9005 32.7549 14.0256 32.7549 12C32.7549 9.97464 33.4034 8.09952 34.5 6.56952C35.5966 8.09952 36.2451 9.97464 36.2451 12C36.2451 14.0256 35.5966 15.9005 34.5 17.4305ZM49.5415 17.4305C48.4449 15.9005 47.7965 14.0256 47.7965 12C47.7965 9.97464 48.4449 8.09952 49.5415 6.56952C50.6381 8.09952 51.2866 9.97464 51.2866 12C51.2866 14.0256 50.6381 15.9005 49.5415 17.4305ZM57.0623 0C54.2135 0 51.5958 1.00968 49.5415 2.68968C47.4873 1.00968 44.8696 0 42.0208 0C39.1719 0 36.5542 1.00968 34.5 2.68968C32.4458 1.00968 29.8278 0 26.9792 0C24.1304 0 21.5127 1.00968 19.4585 2.68968C17.4042 1.00968 14.7863 0 11.9377 0C5.35526 0 0 5.3832 0 12C0 18.617 5.35526 24 11.9377 24C14.7863 24 17.4042 22.9906 19.4585 21.3103C21.5127 22.9906 24.1304 24 26.9792 24C29.8278 24 32.4458 22.9906 34.5 21.3103C36.5542 22.9906 39.1719 24 42.0208 24C44.8696 24 47.4873 22.9906 49.5415 21.3103C51.5958 22.9906 54.2135 24 57.0623 24C63.6447 24 69 18.617 69 12C69 5.3832 63.6447 0 57.0623 0Z"
        fill="white"
      />
    </svg>
  );
};

export default AudiHeaderLogo;
